import React, { FC } from 'react';
import TooltipWrapper from 'components/TooltipWrapper';
import { FaEdit } from 'react-icons/fa';
import FetchTable from 'components/table/FetchTable';
import { Column, CellProps } from 'react-table';
import Button from 'components/Button';
import { useHistory } from 'react-router';

import { buildClientEntity, buildClientUrl } from 'utils/helpers/client';
import { useActiveClientContext } from 'store/active-client';

import DeleteEntity from 'components/crud/DeleteEntity';
import { PermissionKeys } from 'store/types';
import { usePermissionsContext } from 'store/permissions';
import { useTranslation } from 'react-i18next';
import InnerWrapper from '../../InnerWrapper';
import { IRole } from '../types';

const Screen: FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { client } = useActiveClientContext();
  const { checkPermission } = usePermissionsContext();

  const createRole = () => {
    const url = buildClientUrl('/structure/roles/create', client);
    history.push(url);
  };

  const columns: Array<Column<IRole>> = [
    {
      Header: 'ID',
      accessor: 'id',
    },
    {
      Header: t('structure.roles.name') || '',
      accessor: 'name',
      width: 'calc(100% - 7.5rem)',
      Cell: (cell: CellProps<IRole>) => {
        return <span className="text-break">{cell.value}</span>;
      },
    },
    {
      Header: () => <div className="text-right">{t('table.actions')}</div>,
      accessor: 'actions',
      width: '7.5rem',
      disableSortBy: true,
      Cell: (cell: CellProps<IRole>) => {
        const { id, name } = cell.row.original;
        const url = buildClientUrl(`/structure/roles/edit/${id}`, client);
        const hasPermission = checkPermission(PermissionKeys.CAN_MANAGE_ROLES);
        return (
          <div className="text-right">
            <div className="d-inline-block mr-2">
              <TooltipWrapper
                id="tooltip-edit"
                overlay={hasPermission ? t('common.edit') : t('common.noPermission')}
              >
                <Button
                  btnType="primary"
                  size="sm"
                  outlined
                  disabled={!hasPermission}
                  onClick={() => history.push(url)}
                >
                  <FaEdit size={14} />
                </Button>
              </TooltipWrapper>
            </div>
            <DeleteEntity
              id={id}
              entity="roles"
              successMsg={t('structure.roles.deleteSuccessMsg')}
              confirmHeading={t('structure.roles.deleteConfirmTitle')}
              confirmMsg={
                <span>
                  {t('structure.roles.deleteConfirmMsg')}&nbsp;
                  <span className="font-weight-bold">{name}</span>?
                </span>
              }
              queryKey={buildClientEntity('roles', client)}
              hasPermission={PermissionKeys.CAN_MANAGE_ROLES}
            />
          </div>
        );
      },
    },
  ];

  return (
    <InnerWrapper>
      <FetchTable<IRole>
        entityName={buildClientEntity('roles', client)}
        entityEndpoint="roles"
        columns={columns}
        onAdd={createRole}
        addBtnLabel={t('structure.roles.addBtnLabel')}
        pagination
        hasPermission={[PermissionKeys.CAN_MANAGE_ROLES]}
      />
    </InnerWrapper>
  );
};

export default Screen;
